import React, { useState, useEffect, useContext, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import moment from "moment-timezone";
import { AuthContext, AuthContextType } from "../../../contexts/auth-context";
import {
  getAvailability,
  AvailabilityResponse,
  getUpgradePricing,
  ServicePriceOption
} from "../../../actions/stay-actions";
import "./Upgrades.scss";

// Stock images for upgrade cards
const EARLY_CHECKIN_IMAGE = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=600&q=80";
const LATE_CHECKOUT_IMAGE = "https://images.unsplash.com/photo-1551882547-ff40c63fe5fa?w=600&q=80";
const EXTEND_STAY_IMAGE = "https://images.unsplash.com/photo-1582719508461-905c673771fd?w=600&q=80";
const HERO_IMAGE = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=1200&q=80";
const DEFAULT_SERVICE_IMAGE = "https://images.unsplash.com/photo-1584132967334-10e028bd69f7?w=600&q=80";

function Upgrades() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useContext<AuthContextType>(AuthContext);
  const stayScrollRef = useRef<HTMLDivElement>(null);
  const servicesScrollRef = useRef<HTMLDivElement>(null);

  const [services, setServices] = useState<ServicePriceOption[]>([]);
  const [currency, setCurrency] = useState<string>("USD");
  const [isLoadingServices, setIsLoadingServices] = useState(true);
  const [availability, setAvailability] = useState<AvailabilityResponse | null>(null);

  // Determine if we're before the arrival date or after first day of stay
  const today = moment().startOf('day');
  const arrivalDate = user?.checkIn ? moment(user.checkIn).startOf('day') : null;
  const isBeforeArrival = arrivalDate ? today.isBefore(arrivalDate) : false;
  const isAfterFirstDayOfStay = arrivalDate ? today.isAfter(arrivalDate) : false;

  // Fetch services from upgrade pricing
  useEffect(() => {
    const fetchServices = async () => {
      try {
        const response = await getUpgradePricing();
        setServices(response.data.servicePrices || []);
        setCurrency(response.data.currency || "USD");
      } catch (err) {
        console.error("Error fetching services:", err);
      } finally {
        setIsLoadingServices(false);
      }
    };

    fetchServices();
  }, []);

  // Fetch availability for stay extension (only after first day of stay)
  useEffect(() => {
    const fetchAvailability = async () => {
      if (!user?.checkOut || !isAfterFirstDayOfStay) return;
      try {
        const response = await getAvailability(user.checkOut);
        setAvailability(response.data);
      } catch (err) {
        console.error("Error fetching availability:", err);
      }
    };

    fetchAvailability();
  }, [user?.checkOut, isAfterFirstDayOfStay]);

  // Handle upgrade card click - navigate to stay page with mode
  const handleUpgradeClick = (upgradeId: string) => {
    switch (upgradeId) {
      case "early-checkin":
        navigate("/admin/stay?mode=early");
        break;
      case "late-checkout":
        navigate("/admin/stay?mode=late");
        break;
      case "extend-stay":
        navigate("/admin/stay?mode=extend");
        break;
      default:
        break;
    }
  };

  // Build stay upgrade options based on timing
  const stayUpgrades: Array<{
    id: string;
    title: string;
    description: string;
    image: string;
    available: boolean;
  }> = [];

  // BEFORE ARRIVAL: Show only early check-in
  if (isBeforeArrival) {
    stayUpgrades.push({
      id: "early-checkin",
      title: t("upgradesPage.earlyCheckin"),
      description: t("upgradesPage.earlyCheckinDesc"),
      image: EARLY_CHECKIN_IMAGE,
      available: true
    });
  }

  // AFTER ARRIVAL: Show late checkout and extend stay
  if (isAfterFirstDayOfStay) {
    stayUpgrades.push({
      id: "late-checkout",
      title: t("upgradesPage.lateCheckout"),
      description: t("upgradesPage.lateCheckoutDesc"),
      image: LATE_CHECKOUT_IMAGE,
      available: true
    });

    // Only show extend stay if there's availability
    if (availability && availability.availableDays > 0) {
      stayUpgrades.push({
        id: "extend-stay",
        title: t("upgradesPage.extendStay"),
        description: t("upgradesPage.extendStayDesc", { count: availability.availableDays }),
        image: EXTEND_STAY_IMAGE,
        available: true
      });
    }
  }

  // If no upgrades available (edge case), show placeholder
  if (stayUpgrades.length === 0) {
    stayUpgrades.push({
      id: "placeholder",
      title: t("upgradesPage.stayUpgrades"),
      description: t("upgradesPage.stayUpgradesPlaceholder"),
      image: EARLY_CHECKIN_IMAGE,
      available: false
    });
  }

  return (
    <div className="upgrades-page dark-theme">
      {/* Hero Section */}
      <div className="upgrades-hero">
        <div
          className="upgrades-hero__background"
          style={{ backgroundImage: `url(${HERO_IMAGE})` }}
        />
        <div className="upgrades-hero__overlay" />
        <div className="upgrades-hero__content">
          <h1 className="upgrades-hero__title">{t("upgrades.title")}</h1>
        </div>
      </div>

      {/* Main Content */}
      <div className="upgrades-content">
        {/* Customize Your Stay Section */}
        <section className="upgrades-section">
          <h2 className="upgrades-section__label">{t("upgradesPage.customizeStay")}</h2>
          <div className="horizontal-scroll" ref={stayScrollRef}>
            {stayUpgrades.map((upgrade) => (
              <div
                key={upgrade.id}
                className={`upgrade-card-large ${!upgrade.available ? 'upgrade-card-large--disabled' : ''}`}
                onClick={() => upgrade.available && handleUpgradeClick(upgrade.id)}
              >
                <div
                  className="upgrade-card-large__image"
                  style={{ backgroundImage: `url(${upgrade.image})` }}
                />
                <div className="upgrade-card-large__content">
                  <h3>{upgrade.title}</h3>
                  <p>{upgrade.description}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Services Section */}
        <section className="upgrades-section">
          <h2 className="upgrades-section__label">{t("upgradesPage.services")}</h2>
          {isLoadingServices ? (
            <div className="loading-state">
              <div className="loading-spinner" />
            </div>
          ) : services.length > 0 ? (
            <div className="horizontal-scroll" ref={servicesScrollRef}>
              {services.map((service) => (
                <div
                  key={service.code}
                  className="upgrade-card-large"
                  onClick={() => navigate(`/admin/service/${service.code}`)}
                >
                  <div
                    className="upgrade-card-large__image"
                    style={{ backgroundImage: `url(${service.photoUrl || DEFAULT_SERVICE_IMAGE})` }}
                  />
                  <div className="upgrade-card-large__content">
                    <h3>{service.label}</h3>
                    <p>{service.frequency === 'once' ? t("upgradesPage.oneTimeService") : t("upgradesPage.recurringService", { frequency: service.frequency })}</p>
                    <span className="upgrade-card-large__price">${(service.priceCents / 100).toFixed(2)} {currency}</span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <p>{t("upgradesPage.noServices")}</p>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}

export default Upgrades;
